/**
 * @tokamio/sdk — the public contract between a game and the platform.
 *
 * Every exported symbol in this file is a versioned promise to fourteen games.
 * Adding is cheap. Changing or removing is a breaking release. Do not edit this
 * surface as a side effect of a game task.
 *
 * Implementation lives in `./internal/`. This file is the contract only.
 */

export const SDK_CONTRACT_VERSION = 1 as const;

export type Millis = number;
export type Unsubscribe = () => void;

/** Whole Toks. Never fractional, never negative. */
export type TokAmount = number;

export interface TokamioConfig {
  /** Stable slug, matches the directory name and `game.json`. */
  gameId: string;
  /** Semver of the game build. Sent with every event and run. */
  gameVersion: string;
  /** Version of the simulation. Bump when a change invalidates old replays. */
  simVersion: number;
  env?: "dev" | "prod";
}

/* ------------------------------------------------------------------ session */

export interface Player {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  /** Guests can play and earn, but cannot spend or appear on global boards. */
  isGuest: boolean;
}

export interface SessionApi {
  readonly player: Player | null;
  readonly isSignedIn: boolean;
  /**
   * Opens the portal sign-in overlay. Pauses the game for the duration.
   * `reason` is shown to the player, e.g. "to save your progress".
   */
  requireSignIn(reason: string): Promise<Player>;
  onChange(cb: (player: Player | null) => void): Unsubscribe;
}

/* --------------------------------------------------------------------- toks */

export interface SpendRequest {
  amount: TokAmount;
  /** Catalogue id of the thing being bought. Must exist server-side. */
  sku: string;
  /** Caller-generated, stable across retries. Prevents double spends. */
  idempotencyKey: string;
}

export type SpendResult =
  | { ok: true; balance: TokAmount }
  | {
      ok: false;
      reason: "insufficient_funds" | "unknown_sku" | "guest" | "offline" | "rejected";
      balance: TokAmount;
    };

/**
 * Toks are shared across every game on the portal, so the client is never
 * trusted with the balance. There is deliberately no `grant` method: Toks are
 * awarded server-side when a run summary passes validation.
 */
export interface ToksApi {
  /** Last known server balance. Display only. Never gate logic on this alone. */
  readonly balance: TokAmount;
  refresh(): Promise<TokAmount>;
  onChange(cb: (balance: TokAmount) => void): Unsubscribe;
  spend(req: SpendRequest): Promise<SpendResult>;
}

/* ------------------------------------------------------------------- scores */

export interface RunSummary {
  /** The seed the run was played with. Required for replay verification. */
  seed: number;
  startedAt: number;
  durationMs: Millis;
  score: number;
  /**
   * Gameplay counters the server uses for plausibility checks, e.g.
   * `{ kills: 412, damageTaken: 88, wavesCleared: 9 }`. Keep the key set stable.
   */
  stats: Readonly<Record<string, number>>;
  /** Hash of the recorded input stream, produced by the sim, not the renderer. */
  inputHash: string;
  simVersion: number;
}

export interface SubmitResult {
  accepted: boolean;
  rank: number | null;
  toksAwarded: TokAmount;
  /** Present when `accepted` is false. Show nothing to the player but a retry. */
  reason?: "implausible" | "stale_sim_version" | "rate_limited" | "offline";
}

export interface LeaderboardQuery {
  scope: "game" | "global" | "friends";
  period: "daily" | "weekly" | "alltime";
  limit?: number;
  cursor?: string;
}

export interface LeaderboardEntry {
  rank: number;
  player: Player;
  score: number;
  achievedAt: number;
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  /** The signed-in player's own row, even when outside the returned page. */
  viewer: LeaderboardEntry | null;
  nextCursor: string | null;
}

export interface ScoresApi {
  submitRun(run: RunSummary): Promise<SubmitResult>;
  leaderboard(q: LeaderboardQuery): Promise<LeaderboardPage>;
}

/* -------------------------------------------------------------------- saves */

export interface SaveCodec<T> {
  /** Bump on every shape change, and add a branch to `migrate`. */
  version: number;
  /** Fresh save for a new player. */
  create(): T;
  /** Upgrade a save written by an older version. Must handle every prior version. */
  migrate(raw: unknown, fromVersion: number): T;
}

export interface SaveHandle<T> {
  readonly data: Readonly<T>;
  /** Mutate through this. Marks dirty and schedules a debounced flush. */
  mutate(fn: (draft: T) => void): void;
  /** Force an immediate write. Call before a run ends or the tab unloads. */
  flush(): Promise<void>;
  /** Cloud save differs from local. Return the save to keep. */
  onConflict(resolve: (remote: T, local: T) => T): Unsubscribe;
}

export interface SavesApi {
  open<T>(codec: SaveCodec<T>): Promise<SaveHandle<T>>;
}

/* ------------------------------------------------------------------- events */

/** Extend deliberately. Every key here becomes a column in analytics forever. */
export interface GameEventMap {
  run_start: { seed: number };
  run_end: { durationMs: Millis; score: number; outcome: "win" | "lose" | "quit" };
  progression: { milestone: string; value: number };
  purchase_intent: { sku: string; price: TokAmount };
  ad_shown: { placement: string; kind: AdKind };
  error: { message: string; fatal: boolean };
}

export interface EventsApi {
  track<K extends keyof GameEventMap>(name: K, props: GameEventMap[K]): void;
}

/* ---------------------------------------------------------------------- ads */

export type AdKind = "interstitial" | "rewarded";

export type AdResult =
  | { shown: true; completed: boolean }
  | { shown: false; reason: "not_ready" | "blocked" | "capped" | "guest" };

/**
 * The SDK pauses the lifecycle and mutes the audio bus for the duration of an
 * ad, then resumes. Do not do that yourself, and never award anything for a
 * rewarded ad until `completed` is true.
 */
export interface AdsApi {
  isReady(kind: AdKind): boolean;
  show(kind: AdKind, placement: string): Promise<AdResult>;
}

/* ---------------------------------------------------------------- lifecycle */

export type PauseReason = "menu" | "blur" | "ad" | "dialog" | "sign_in";

export interface LifecycleApi {
  readonly isPaused: boolean;
  /** Reference counted. Two pauses need two resumes. */
  pause(reason: PauseReason): void;
  resume(reason: PauseReason): void;
  onPause(cb: (reason: PauseReason) => void): Unsubscribe;
  onResume(cb: (reason: PauseReason) => void): Unsubscribe;
  /** Tab is being discarded. Persist synchronously; async work will not finish. */
  onBeforeUnload(cb: () => void): Unsubscribe;
}

/* ---------------------------------------------------------------------- sdk */

export interface TokamioSdk {
  readonly contractVersion: typeof SDK_CONTRACT_VERSION;
  readonly session: SessionApi;
  readonly toks: ToksApi;
  readonly scores: ScoresApi;
  readonly saves: SavesApi;
  readonly events: EventsApi;
  readonly ads: AdsApi;
  readonly lifecycle: LifecycleApi;
  /** Releases every listener the SDK installed. Call on game teardown. */
  destroy(): void;
}

export async function createTokamio(config: TokamioConfig): Promise<TokamioSdk> {
  const { createTokamio: createInternal } = await import("./internal/index");
  return createInternal(config);
}
